/**
 * MeetingWizardPage — F-Meeting-1 Wave 2 (AC-4.4)
 *
 * BACK-LINK CONVENTION (AC-MUT2-9, supersedes AC-FW3a-6-1/FW3a-5):
 *   All wizard exit points return to the navigation origin (calendar, meetings list, etc.).
 *   returnTo is resolved from: location.state → sessionStorage(keyed by meeting id) → /roros/meetings.
 *   MeetingChip passes the current URL as returnTo state (one-line change, do-not-touch lifted).
 *   Persisting to sessionStorage makes reload/resume flows durable.
 *
 * Facilitation wizard at /roros/meetings/:id/run.
 *
 * Layout:
 *   Header: meeting title + cadence type + "Step N of M" progress
 *   Two-pane: Left (60%) active agenda item + response; Right (40%) read-only context
 *   Footer: "Save & Pause" | "Save & Continue"
 *
 * Page logic:
 *   1. Load meeting via GET /api/v1/meetings/:id
 *   2. If scheduled → call start-facilitation automatically
 *   3. If in_progress or paused → use as-is
 *   4. If terminal → show completion message
 *   5. "Save & Continue" → POST /:id/agenda/:itemId/advance
 *   6. "Save & Pause" → POST /:id/pause → navigate back
 *   7. After advance: reload meeting, update to next item; if completed → show completion screen
 *
 * AI Draft (AC-5.3):
 *   - "Draft with Virtual CEO" button for annual_goal_text items
 *   - POST /:id/draft with context/seed/references
 *   - If response.notice → AI not configured, disable subsequent calls
 */

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useParams, useNavigate, useLocation, Link } from 'react-router-dom';
import {
  Play,
  Pause,
  ChevronRight,
  ChevronDown,
  ChevronUp,
  Sparkles,
  Loader2,
  CheckCircle2,
  ArrowLeft,
  Plus,
  X,
  RotateCcw,
} from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { Textarea } from '@/components/ui/Textarea/Textarea';
import { t } from '@/lib/i18n';
import { apiFetch } from '@/lib/api';
import type {
  RorosMeetingDTO,
  AgendaItemWithFacilitation,
  MeetingStatus,
  PendingOutcome,
} from '@/types/roros/meetings';
import { useAnnualOutcomes } from '@/hooks/roros/artifacts';
import { Badge } from '@/components/ui/Badge';
import { getActiveOrgId } from '@/stores/orgStore';
import { CompanyContextPanel } from '@/components/roros/alignment/CompanyContextPanel';
// CCA-M2 AC-CCA-21
import { AlignmentWarnings } from '@/components/roros/alignment/AlignmentWarnings';
import type { AlignmentConflict } from '@/services/roros/alignment.service';
import { outcomeStatusVariant, outcomeStatusLabel } from '@/lib/roros/outcomeHelpers';

// ─── Types ────────────────────────────────────────────────────────────────────

interface DraftResult {
  drafts: string[];
  modelUsed: string | null;
  tokensUsed: number;
  notice?: string;
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

function meetingTypeLabel(type: string): string {
  const map: Record<string, string> = {
    daily:     t('Daily Check-in'),
    weekly:    t('Weekly'),
    monthly:   t('Monthly'),
    quarterly: t('Quarterly'),
    trimester: t('Trimester'),
    annual:    t('Annual'),
    ad_hoc:    t('Ad hoc'),
  };
  return map[type] ?? type;
}

function isTerminalMeetingStatus(status: MeetingStatus | string): boolean {
  return status === 'completed' || status === 'skipped' || status === 'cancelled';
}

// ─── Right pane: Read-only context panel ─────────────────────────────────────

/**
 * ContextPanel — right-pane content for meeting facilitation.
 *
 * Top: <CompanyContextPanel> (Governance / Company / Direction / Market)
 *      — same component as Decisions and Outcomes, replaces the old
 *        hand-rolled Purpose/Vision/Core Values/Principles blocks.
 *
 * Below: Annual Outcomes (AC-8.3) — facilitation working-set, kept as-is.
 */
function ContextPanel() {
  // AC-8.3: Annual Outcomes — always visible, active filter, compact list
  const annualOutcomesQuery = useAnnualOutcomes({ time_horizon: 'annual', status: 'active' });
  const annualOutcomes = annualOutcomesQuery.data ?? [];

  return (
    <div className="space-y-5 text-sm">
      {/* Company Context — GOVERNANCE / COMPANY / DIRECTION / MARKET
          Replaces the old Purpose / Vision / Core Values / Principles blocks.
          Loading state + empty nudge are handled internally by CompanyContextPanel. */}
      <CompanyContextPanel data-testid="meeting-company-context-panel" />

      {/* Annual Outcomes (AC-8.3) — meeting facilitation working-set */}
      {/* AC-MUT2-10: text-foreground for readable contrast on bg-background pane */}
      <div>
        <p className="text-xs font-semibold text-foreground uppercase tracking-wide mb-2">
          {t('Annual Outcomes')}
        </p>
        {annualOutcomesQuery.isLoading ? (
          <div className="flex items-center gap-2">
            <Loader2 className="h-3.5 w-3.5 animate-spin text-muted-foreground" />
            <span className="text-xs text-muted-foreground">{t('Loading...')}</span>
          </div>
        ) : annualOutcomes.length === 0 ? (
          <p className="text-xs text-muted-foreground italic">
            {t('No active annual outcomes yet.')}
          </p>
        ) : (
          <ul className="space-y-1.5" data-testid="wizard-annual-outcomes-list">
            {annualOutcomes.map((outcome) => (
              <li key={outcome.id} className="flex items-center justify-between gap-2">
                <span className="text-xs text-foreground leading-tight truncate min-w-0">
                  {outcome.title}
                </span>
                <Badge variant={outcomeStatusVariant(outcome.status)} className="shrink-0 text-[10px] px-1.5 py-0">
                  {outcomeStatusLabel(outcome.status)}
                </Badge>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}

// ─── AI Draft section (AC-5.3) ────────────────────────────────────────────────

interface AiDraftSectionProps {
  meetingId: string;
  agendaItemId: string;
  inputType: 'annual_goal_text' | 'outcome_text_per_goal';
  currentText: string;
  onUseDraft: (text: string) => void;
  aiConfigured: boolean;
}

function AiDraftSection({ meetingId, agendaItemId, inputType, currentText, onUseDraft, aiConfigured }: AiDraftSectionProps) {
  const [isDrafting, setIsDrafting] = useState(false);
  const [drafts, setDrafts] = useState<string[]>([]);
  // FW2-7: Initialize from aiConfigured prop so button is disabled from first render when AI is not configured
  const [aiUnavailable, setAiUnavailable] = useState(!aiConfigured);

  // FW2-1: Compute context enum from inputType
  const contextEnum = inputType === 'annual_goal_text' ? 'annual_goal' : 'outcome';

  const handleDraft = useCallback(async () => {
    setIsDrafting(true);
    setDrafts([]);
    try {
      const res = await apiFetch(`/api/v1/meetings/${meetingId}/draft`, {
        method: 'POST',
        // FW2-1: Fix POST body — correct context/seed/references shape
        body: JSON.stringify({
          context: contextEnum,
          seed: currentText.trim() || '',
          references: {},
        }),
      });
      if (!res.ok) {
        throw new Error(t('Draft request failed'));
      }
      const json = await res.json() as { data: DraftResult };
      if (json.data.notice) {
        // AI provider not configured
        setAiUnavailable(true);
        return;
      }
      setDrafts(json.data.drafts);
    } catch (err) {
      console.error('[MeetingWizardPage] draft error', err);
    } finally {
      setIsDrafting(false);
    }
  }, [meetingId, contextEnum, currentText]);

  if (aiUnavailable) {
    return (
      <div className="flex items-center gap-2 text-xs text-muted-foreground">
        <Sparkles className="h-3.5 w-3.5 shrink-0" />
        <span title={t('Configure AI provider in Settings')}>
          {t('AI drafting unavailable — configure AI provider in Settings')}
        </span>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      <Button
        variant="outline"
        size="sm"
        onClick={handleDraft}
        isLoading={isDrafting}
        leftIcon={<Sparkles className="h-3.5 w-3.5" />}
        // FW2-1: Disable when seed is empty (seed is required per brief)
        disabled={isDrafting || !currentText.trim()}
      >
        {t('Draft with Virtual CEO')}
      </Button>

      {drafts.length > 0 && (
        <div className="space-y-2">
          <p className="text-xs font-medium text-muted-foreground">{t('Select a draft to use:')}</p>
          {drafts.map((draft, idx) => (
            <div key={idx} className="rounded-md border border-border bg-muted/30 p-3 space-y-2">
              <p className="text-sm text-foreground leading-relaxed whitespace-pre-wrap">{draft}</p>
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  onUseDraft(draft);
                  setDrafts([]);
                }}
              >
                {t('Use this')}
              </Button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ─── AC-3b-1-5: Disposition step sub-component ───────────────────────────────
//
// Extracted into its own component so that useQuery and useState are called at
// the top level of a component, satisfying Rules of Hooks. ResponseArea renders
// this for the 'outcome_disposition_table' inputType.

interface DispositionStepInputProps {
  onAdvance: (payload: unknown) => Promise<void>;
  isSaving?: boolean;
}

// PendingOutcome type moved to src/types/roros/meetings.ts (AC-3b-1-5 B5)

function DispositionStepInput({ onAdvance, isSaving }: DispositionStepInputProps) {
  const { data: outcomes = [], isLoading, isError } = useQuery({
    queryKey: ['roros', 'outcomes', 'pending-disposition'],
    queryFn: async () => {
      const res = await apiFetch('/api/v1/outcomes/pending-disposition');
      if (!res.ok) {
        throw new Error(`Failed to load pending dispositions: ${res.status}`);
      }
      const json = await res.json() as { success: boolean; data: { outcomes: PendingOutcome[] } };
      return json.data?.outcomes ?? [];
    },
  });

  const [selections, setSelections] = useState<Record<string, 'continue' | 'archive'>>({});

  if (isError) {
    return (
      <div className="p-4 text-destructive">
        {t('Failed to load outcomes. Please refresh or skip this step.')}
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="flex items-center gap-2 text-sm text-muted-foreground">
        <Loader2 className="h-4 w-4 animate-spin" />
        {t('Loading...')}
      </div>
    );
  }

  if (outcomes.length === 0) {
    return (
      <div className="space-y-3" data-testid="disposition-empty-state">
        <p className="text-sm text-muted-foreground italic">
          {t('No previous outcomes to review.')}
        </p>
        <Button
          variant="primary"
          size="sm"
          disabled={isSaving}
          data-testid="disposition-empty-continue"
          onClick={() => void onAdvance({ skipped: true })}
        >
          {t('Continue')}
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="disposition-table-container">
      <h3 className="text-sm font-semibold">{t('Previous Cycle Review')}</h3>
      <div className="rounded-md border overflow-hidden">
        <table className="w-full text-sm" data-testid="disposition-table">
          <thead className="bg-muted/50">
            <tr>
              <th className="text-left px-3 py-2 font-medium">{t('Title')}</th>
              <th className="text-left px-3 py-2 font-medium">{t('Status')}</th>
              <th className="px-3 py-2 font-medium">{t('Decision')}</th>
            </tr>
          </thead>
          <tbody>
            {outcomes.map((outcome) => (
              <tr key={outcome.id} className="border-t" data-testid={`disposition-row-${outcome.id}`}>
                <td className="px-3 py-2">
                  <div>{outcome.title}</div>
                  {outcome.domain_name && (
                    <div className="text-xs text-muted-foreground">{outcome.domain_name}</div>
                  )}
                </td>
                <td className="px-3 py-2">
                  <Badge variant={outcomeStatusVariant(outcome.status)}>
                    {outcomeStatusLabel(outcome.status)}
                  </Badge>
                </td>
                <td className="px-3 py-2">
                  <div className="flex gap-2 justify-center">
                    <Button
                      size="sm"
                      variant={selections[outcome.id] === 'continue' ? 'primary' : 'outline'}
                      disabled={isSaving}
                      data-testid={`disposition-continue-${outcome.id}`}
                      onClick={() => setSelections((prev) => ({ ...prev, [outcome.id]: 'continue' }))}
                    >
                      {t('Continue')}
                    </Button>
                    <Button
                      size="sm"
                      variant={selections[outcome.id] === 'archive' ? 'destructive' : 'outline'}
                      disabled={isSaving}
                      data-testid={`disposition-archive-${outcome.id}`}
                      onClick={() => setSelections((prev) => ({ ...prev, [outcome.id]: 'archive' }))}
                    >
                      {t('Archive')}
                    </Button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="flex gap-2">
        <Button
          variant="primary"
          disabled={isSaving}
          data-testid="disposition-save-continue"
          onClick={() => void onAdvance({
            decisions: Object.entries(selections).map(([outcomeId, decision]) => ({ outcomeId, decision })),
            skipped: false,
          })}
        >
          {t('Save & Continue')}
        </Button>
        {/* AC-FW3a-5-1: Skip immediately calls the advance endpoint — no second footer click needed */}
        <Button
          variant="outline"
          size="sm"
          disabled={isSaving}
          data-testid="disposition-skip"
          onClick={() => void onAdvance({ skipped: true })}
        >
          {t('Skip')}
        </Button>
      </div>
    </div>
  );
}

// ─── Response area per inputType ─────────────────────────────────────────────

interface ResponseAreaProps {
  item: AgendaItemWithFacilitation;
  meetingId: string;
  response: unknown;
  onChange: (val: unknown) => void;
  /** AC-FW3a-5-1/2: Direct advance helper — Skip and Confirm call this instead of onChange */
  onAdvance: (payload: unknown) => Promise<void>;
  isSaving?: boolean;
  aiConfigured: boolean;
}

function ResponseArea({ item, meetingId, response, onChange, onAdvance, isSaving, aiConfigured }: ResponseAreaProps) {
  const inputType = item.inputType;

  if (inputType === 'outcome_disposition_table') {
    return <DispositionStepInput onAdvance={onAdvance} isSaving={isSaving} />;
  }

  if (inputType === 'annual_goal_text') {
    const text = typeof response === 'string' ? response : '';
    return (
      <div className="space-y-3">
        <Textarea
          data-roros-input="wizard-textarea-annual"
          className="min-h-[120px]"
          placeholder={t('Enter your annual goal...')}
          value={text}
          onChange={(e) => onChange(e.target.value)}
        />
        <AiDraftSection
          meetingId={meetingId}
          agendaItemId={item.id}
          inputType="annual_goal_text"
          currentText={text}
          onUseDraft={(draft) => onChange(draft)}
          aiConfigured={aiConfigured}
        />
      </div>
    );
  }

  if (inputType === 'outcome_text_per_goal') {
    const rows = Array.isArray(response) && response.length > 0 ? (response as string[]) : [''];
    return (
      <div className="space-y-3">
        {rows.map((row, idx) => (
          <div key={idx} className="flex gap-2">
            <Textarea
              data-roros-input="wizard-textarea-outcome"
              className="flex-1 min-h-[80px]"
              placeholder={`${t('Outcome for goal')} ${idx + 1}`}
              value={row}
              onChange={(e) => {
                const next = [...rows];
                next[idx] = e.target.value;
                onChange(next);
              }}
            />
            {rows.length > 1 && (
              <Button
                type="button"
                variant="ghost"
                size="icon"
                className="self-start mt-2"
                onClick={() => {
                  const next = rows.filter((_, i) => i !== idx);
                  onChange(next);
                }}
                aria-label={t('Remove row')}
              >
                <X className="h-4 w-4" />
              </Button>
            )}
          </div>
        ))}
        <Button
          variant="dashed"
          size="sm"
          leftIcon={<Plus className="h-4 w-4" />}
          onClick={() => onChange([...rows, ''])}
        >
          {t('Add another')}
        </Button>
      </div>
    );
  }

  if (inputType === 'confirm_cascade') {
    return (
      <div className="space-y-3">
        <p className="text-sm text-muted-foreground">
          {t('Review the completed items above and confirm to cascade the outcomes.')}
        </p>
        {/* AC-FW3a-5-2: Confirm immediately calls the advance endpoint, which triggers _autoCompleteMeeting + cascade */}
        <Button
          variant="primary"
          size="sm"
          leftIcon={<CheckCircle2 className="h-4 w-4" />}
          disabled={isSaving}
          onClick={() => void onAdvance({ confirmed: true })}
        >
          {t('Confirm')}
        </Button>
      </div>
    );
  }

  // Default: state_recheck and any other type — plain textarea
  const text = typeof response === 'string' ? response : '';
  return (
    <Textarea
      data-roros-input="wizard-textarea-default"
      className="min-h-[120px]"
      placeholder={t('Add your comments or notes here...')}
      value={text}
      onChange={(e) => onChange(e.target.value)}
    />
  );
}

// ─── Main page ────────────────────────────────────────────────────────────────

export function MeetingWizardPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();

  // AC-MUT2-9: returnTo — resolve from: location.state → sessionStorage(keyed by meeting id) → /roros/meetings.
  // MeetingChip passes state.returnTo when navigating from the calendar.
  // sessionStorage ensures reload and pause→resume flows stay on the correct origin.
  const storageKey = id ? `meeting-returnTo-${id}` : null;
  const returnTo: string = useMemo(() => {
    const fromState = (location.state as { returnTo?: string } | null)?.returnTo;
    if (fromState) return fromState;
    if (storageKey) {
      try {
        const stored = sessionStorage.getItem(storageKey);
        if (stored) return stored;
      } catch { /* sessionStorage unavailable (e.g. strict private mode) */ }
    }
    return '/roros/meetings';
  }, [storageKey, location.state]);

  // Persist returnTo to sessionStorage on mount (once per meeting id) so reloads stay durable.
  useEffect(() => {
    if (storageKey && returnTo !== '/roros/meetings') {
      try {
        sessionStorage.setItem(storageKey, returnTo);
      } catch { /* ignore */ }
    }
  }, [storageKey, returnTo]);

  const [meeting, setMeeting] = useState<RorosMeetingDTO | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [isPausing, setIsPausing] = useState(false);
  const [isResuming, setIsResuming] = useState(false);
  const [contextExpanded, setContextExpanded] = useState(true);

  // FW2-7: AI config state — optimistic default (true) so button is enabled while checking
  const [aiConfigured, setAiConfigured] = useState(true);

  // Current item response state
  const [currentResponse, setCurrentResponse] = useState<unknown>('');

  // CCA-M2 AC-CCA-21: non-blocking alignment warnings from advance
  const [advanceAlignmentWarnings, setAdvanceAlignmentWarnings] = useState<AlignmentConflict[]>([]);

  // ── Load meeting ──

  const loadMeeting = useCallback(async (meetingId: string): Promise<RorosMeetingDTO> => {
    const res = await apiFetch(`/api/v1/meetings/${meetingId}`);
    if (!res.ok) {
      throw new Error(t('Failed to load meeting'));
    }
    const json = await res.json() as { data: RorosMeetingDTO };
    return json.data;
  }, []);

  // ── Initialize ──

  useEffect(() => {
    if (!id) return;

    let cancelled = false;

    async function init() {
      setIsLoading(true);
      setError(null);
      try {
        let m = await loadMeeting(id!);
        if (cancelled) return;

        // If scheduled → start facilitation automatically
        if (m.status === 'scheduled') {
          const startRes = await apiFetch(`/api/v1/meetings/${id}/start-facilitation`, {
            method: 'POST',
          });
          if (!startRes.ok) {
            const errJson = await startRes.json().catch(() => ({})) as { error?: { message?: string } };
            throw new Error(errJson.error?.message ?? t('Failed to start facilitation'));
          }
          const startJson = await startRes.json() as { data: RorosMeetingDTO };
          m = startJson.data;
        }

        if (!cancelled) {
          setMeeting(m);
          // AC-4A-2: Restore the draft saved on the most recent "Save & Pause"
          // so the facilitator sees their in-progress text when they resume.
          const draft = m.facilitationState?.currentItemDraft;
          setCurrentResponse(typeof draft === 'string' ? draft : '');
        }
      } catch (err) {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : t('An error occurred'));
        }
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    }

    init();
    return () => { cancelled = true; };
  }, [id, loadMeeting]);

  // FW2-7: Check AI config availability on mount
  useEffect(() => {
    const orgId = getActiveOrgId();
    if (!orgId) return;
    apiFetch(`/api/orgs/${orgId}/ai-config/status`)
      .then((res) => res.ok ? res.json() : Promise.reject())
      .then((json: { data: { available: boolean } }) => {
        setAiConfigured(json.data.available);
      })
      .catch(() => {
        // Silently keep default (true) — don't break wizard on AI status check failure
      });
  }, []);

  // ── Derived state ──

  const status = meeting?.status as MeetingStatus | undefined;
  const isTerminal = status ? isTerminalMeetingStatus(status) : false;
  const isCompleted = status === 'completed';

  const agendaItems = (meeting?.agendaItems ?? []) as AgendaItemWithFacilitation[];
  const currentItemId = meeting?.facilitationState?.currentAgendaItemId ?? null;
  const currentItem = agendaItems.find((item) => item.id === currentItemId) ?? null;
  const currentItemIndex = currentItem ? agendaItems.indexOf(currentItem) : -1;
  const stepNumber = currentItemIndex >= 0 ? currentItemIndex + 1 : null;
  const totalSteps = agendaItems.length;

  // ── Advance helper (AC-FW3a-5-1/2) ──
  //
  // Extracted from handleContinue so that the Skip (outcome_disposition_table) and
  // Confirm (confirm_cascade) buttons can call the /advance endpoint directly without
  // requiring a second footer click. handleContinue delegates here too, keeping a
  // single code path for all advance calls.

  async function advanceWithResponse(payload: unknown) {
    if (!id || !currentItem) return;
    setIsSaving(true);
    // Clear previous advance warnings on each new advance
    setAdvanceAlignmentWarnings([]);
    try {
      const res = await apiFetch(`/api/v1/meetings/${id}/agenda/${currentItem.id}/advance`, {
        method: 'POST',
        body: JSON.stringify({ response: payload }),
      });
      if (!res.ok) {
        const errJson = await res.json().catch(() => ({})) as { error?: { message?: string } };
        throw new Error(errJson.error?.message ?? t('Failed to advance'));
      }
      // AC-CCA-21: response now includes optional alignmentWarnings
      const json = await res.json() as { data: RorosMeetingDTO; alignmentWarnings?: AlignmentConflict[] };
      setMeeting(json.data);
      setCurrentResponse('');
      // Non-blocking: show inline warning if any conflicts found (item already committed)
      if (json.alignmentWarnings && json.alignmentWarnings.length > 0) {
        setAdvanceAlignmentWarnings(json.alignmentWarnings);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : t('An error occurred'));
    } finally {
      setIsSaving(false);
    }
  }

  // ── Save & Continue ──

  async function handleContinue() {
    if (!id || !currentItem) return;
    // FW2-2: For annual_goal_text, service requires response as { text: string } object.
    const responsePayload = (currentItem as AgendaItemWithFacilitation).inputType === 'annual_goal_text'
      ? { text: typeof currentResponse === 'string' ? currentResponse.trim() : '' }
      : currentResponse;
    await advanceWithResponse(responsePayload);
  }

  // ── Save & Pause ──
  //
  // AC-4A-2: Sends the current step's draft response in the body so the server
  // can persist it in facilitation_state.currentItemDraft. Verbatim — even empty
  // strings are included (OQ-2 confirmed: empty is a valid verbatim value).

  async function handlePause() {
    if (!id) return;
    setIsPausing(true);
    try {
      const body: { agendaItemId?: string; draft?: unknown } = {};
      if (currentItem) {
        body.agendaItemId = currentItem.id;
        body.draft = currentResponse;
      }
      const res = await apiFetch(`/api/v1/meetings/${id}/pause`, {
        method: 'POST',
        body: JSON.stringify(body),
      });
      if (!res.ok) {
        const errJson = await res.json().catch(() => ({})) as { error?: { message?: string } };
        throw new Error(errJson.error?.message ?? t('Failed to pause'));
      }
      navigate(returnTo);
    } catch (err) {
      setError(err instanceof Error ? err.message : t('An error occurred'));
      setIsPausing(false);
    }
  }

  // ── Resume ──
  //
  // AC-4A-3: Calls POST /:id/resume to transition paused → in_progress.
  // After resuming, the updated meeting is loaded in-page (no navigate) so
  // the facilitator can continue from where they left off.

  async function handleResume() {
    if (!id) return;
    setIsResuming(true);
    try {
      const res = await apiFetch(`/api/v1/meetings/${id}/resume`, {
        method: 'POST',
      });
      if (!res.ok) {
        const errJson = await res.json().catch(() => ({})) as { error?: { message?: string } };
        throw new Error(errJson.error?.message ?? t('Failed to resume meeting'));
      }
      const json = await res.json() as { data: RorosMeetingDTO };
      setMeeting(json.data);
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : t('An error occurred'));
    } finally {
      setIsResuming(false);
    }
  }

  // ── Render states ──

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (error && !meeting) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen gap-4 p-8 text-center">
        <p className="text-error-600 text-lg font-medium">{error}</p>
        <Link to="/roros/meetings" className="text-primary-600 hover:underline text-sm">
          {t('Back to Meetings')}
        </Link>
      </div>
    );
  }

  if (!meeting) {
    return null;
  }

  // Terminal states (skipped or cancelled — NOT completed; isCompleted handled below).
  // AC-FW3a-6-2: Using Option A (generic copy) — avoids branching on status and is
  // truthful for both skipped and cancelled states.
  if (isTerminal && !isCompleted) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen gap-4 p-8 text-center">
        <p className="text-muted-foreground text-lg">{t('This meeting cannot be facilitated.')}</p>
        <Link to="/roros/meetings" className="text-primary-600 hover:underline text-sm">
          {t('Back to Meetings')}
        </Link>
      </div>
    );
  }

  // Completion screen
  // AC-MUT2-6: completion screen link returns to origin (calendar or list) via returnTo state.
  // Fallback: /roros/meetings (list) for direct URL / reload / deep-link.
  if (isCompleted) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen gap-6 p-8 text-center">
        <CheckCircle2 className="h-16 w-16 text-success-500" />
        <div>
          <h1 className="text-2xl font-bold text-foreground mb-2">{t('Meeting completed!')}</h1>
          <p className="text-muted-foreground">{meeting.title}</p>
        </div>
        <Link
          to={returnTo}
          className="inline-flex items-center gap-2 text-primary-600 hover:underline text-sm"
        >
          <ArrowLeft className="h-4 w-4" />
          {t('Back to Meetings')}
        </Link>
      </div>
    );
  }

  // No agenda items
  if (agendaItems.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen gap-4 p-8 text-center">
        <p className="text-muted-foreground text-lg">{t('No agenda items for this meeting.')}</p>
        <Link to="/roros/meetings" className="text-primary-600 hover:underline text-sm">
          {t('Back to Meetings')}
        </Link>
      </div>
    );
  }

  // ── Main wizard layout ──

  return (
    <div className="flex flex-col h-screen overflow-hidden bg-page-bg">
      {/* ── Header ── */}
      <header className="bg-background border-b border-border px-4 md:px-8 py-3 flex items-center justify-between gap-4 shrink-0">
        <div className="flex items-center gap-3 min-w-0">
          {/* AC-MUT2-9: back arrow uses returnTo (calendar, list, etc.) — not hardcoded to meetings */}
          <Link
            to={returnTo}
            className="text-muted-foreground hover:text-foreground transition-colors shrink-0"
            aria-label={t('Back to Meetings')}
          >
            <ArrowLeft className="h-5 w-5" />
          </Link>
          <div className="min-w-0">
            <h1 className="text-base font-semibold text-foreground truncate">{meeting.title}</h1>
            <p className="text-xs text-muted-foreground">
              {meetingTypeLabel(meeting.meetingType)}
            </p>
          </div>
        </div>

        {/* Progress indicator */}
        {stepNumber !== null && totalSteps > 0 && (
          <div className="flex items-center gap-2 shrink-0">
            <span className="text-sm font-medium text-muted-foreground">
              {`${t('Step')} ${stepNumber} ${t('of')} ${totalSteps}`}
            </span>
            <div className="hidden sm:flex items-center gap-0.5">
              {agendaItems.map((_, idx) => (
                <div
                  key={idx}
                  className={[
                    'h-1.5 w-5 rounded-full transition-colors',
                    idx < currentItemIndex
                      ? 'bg-success-500'
                      : idx === currentItemIndex
                      ? 'bg-primary-500'
                      : 'bg-muted',
                  ].join(' ')}
                />
              ))}
            </div>
          </div>
        )}
      </header>

      {/* Error banner */}
      {error && (
        <div className="bg-error-50 border-b border-error-200 px-4 md:px-8 py-2 text-sm text-error-700">
          {error}
        </div>
      )}

      {/* CCA-M2 AC-CCA-21: Non-blocking alignment warnings from advance (item already committed) */}
      {advanceAlignmentWarnings.length > 0 && (
        <div className="bg-warning-50 border-b border-warning-200 px-4 md:px-8 py-3">
          <AlignmentWarnings
            conflicts={advanceAlignmentWarnings}
            acknowledgeEnabled={false}
            data-testid="meeting-advance-alignment-warnings"
          />
          <div className="mt-2 flex justify-end">
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => setAdvanceAlignmentWarnings([])}
              aria-label={t('Dismiss alignment warnings')}
            >
              {t('Dismiss')}
            </Button>
          </div>
        </div>
      )}

      {/* ── Two-pane body ── */}
      {/* LAYOUT-NOTE: MeetingWizardPage is intentionally outside BaseLayout (full-screen facilitation UI).
          `overflow-auto` is correct here — the visual-layout regression spec does not cover /roros/meetings/:id/run. */}
      <div className="flex-1 flex flex-col md:flex-row gap-0 md:overflow-hidden overflow-auto">
        {/* Left pane (60%): Active agenda item */}
        <div className="flex-1 md:w-3/5 p-4 md:p-8 space-y-6">
          {currentItem ? (
            <>
              {/* Item header */}
              <div>
                <h2 className="text-xl font-semibold text-foreground mb-2">
                  {(currentItem as AgendaItemWithFacilitation).titleEn ?? currentItem.title}
                </h2>
                {(currentItem as AgendaItemWithFacilitation).prompt && (
                  <p className="text-sm text-muted-foreground leading-relaxed">
                    {(currentItem as AgendaItemWithFacilitation).prompt}
                  </p>
                )}
              </div>

              {/* Completed items summary (for confirm_cascade) */}
              {(currentItem as AgendaItemWithFacilitation).inputType === 'confirm_cascade' && (
                <div className="rounded-md bg-muted/40 border border-border p-4 space-y-2">
                  <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">
                    {t('Completed items')}
                  </p>
                  {(meeting.facilitationState?.completedItems ?? []).length === 0 ? (
                    <p className="text-sm text-muted-foreground">{t('No items completed yet.')}</p>
                  ) : (
                    <ul className="space-y-1">
                      {(meeting.facilitationState?.completedItems ?? []).map((ci) => {
                        const completedItem = agendaItems.find((a) => a.id === ci.itemId);
                        return (
                          <li key={ci.itemId} className="flex items-center gap-2 text-sm">
                            <CheckCircle2 className="h-4 w-4 text-success-500 shrink-0" />
                            <span>{completedItem?.title ?? ci.itemId}</span>
                          </li>
                        );
                      })}
                    </ul>
                  )}
                </div>
              )}

              {/* Response area */}
              <div>
                <ResponseArea
                  item={currentItem}
                  meetingId={id!}
                  response={currentResponse}
                  onChange={setCurrentResponse}
                  onAdvance={advanceWithResponse}
                  isSaving={isSaving}
                  aiConfigured={aiConfigured}
                />
              </div>
            </>
          ) : (
            /* Meeting in_progress but no current item — e.g. all items done */
            <div className="flex flex-col items-center justify-center h-full gap-4 text-center py-16">
              <CheckCircle2 className="h-12 w-12 text-success-400" />
              <p className="text-muted-foreground">{t('All agenda items have been addressed.')}</p>
            </div>
          )}
        </div>

        {/* Right pane (40%): Read-only context
            Change [2]: added `md:flex md:flex-col md:overflow-hidden` so the right pane
            is height-constrained within the flex row, and the inner content div can scroll
            internally (min-h-0 overflow-y-auto) without the outer page growing.
            AC-MUT2-10: bg-muted/20 → bg-background for readable contrast (gray-on-gray fix).
            CompanyContextPanel internal styling untouched (shared component). */}
        <div className="md:w-2/5 border-t md:border-t-0 md:border-l border-border bg-background md:flex md:flex-col md:overflow-hidden">
          {/* Mobile toggle */}
          <Button
            type="button"
            variant="ghost"
            className="w-full flex items-center justify-between px-4 py-3 text-sm font-medium text-muted-foreground hover:text-foreground transition-colors md:hidden"
            onClick={() => setContextExpanded((prev) => !prev)}
          >
            <span>{t('Show context')}</span>
            {contextExpanded ? (
              <ChevronUp className="h-4 w-4" />
            ) : (
              <ChevronDown className="h-4 w-4" />
            )}
          </Button>

          {/* Change [2]: flex-1 min-h-0 overflow-y-auto → scrolls internally on desktop.
              Change [1]: removed M2 CompanyContextPanel duplicate block.
                          The pre-existing ContextPanel (Purpose/Vision/Values/etc.) already
                          satisfies "context visible during facilitation" (AC-CCA-20 intent). */}
          <div className={[
            'p-4 md:p-6 flex-1 min-h-0 overflow-y-auto',
            contextExpanded ? 'block' : 'hidden md:block',
          ].join(' ')}>
            {/* AC-MUT2-10: text-foreground for readable contrast on bg-background pane */}
            <p className="text-xs font-semibold text-foreground uppercase tracking-wide mb-4">
              {t('Overarching Context')}
            </p>
            <ContextPanel />
          </div>
        </div>
      </div>

      {/* ── Footer ── */}
      <footer className="bg-background border-t border-border px-4 md:px-8 py-4 flex items-center justify-between gap-4 shrink-0">
        {/* AC-4A-3: When paused, show Resume button instead of Save & Pause.
            When in_progress, show Save & Pause as before. */}
        {status === 'paused' ? (
          <Button
            variant="primary"
            onClick={handleResume}
            isLoading={isResuming}
            leftIcon={<RotateCcw className="h-4 w-4" />}
            disabled={isResuming || isSaving}
            data-testid="resume-meeting-btn"
          >
            {t('Resume meeting')}
          </Button>
        ) : (
          <Button
            variant="outline"
            onClick={handlePause}
            isLoading={isPausing}
            leftIcon={<Pause className="h-4 w-4" />}
            disabled={isPausing || isSaving}
            data-testid="save-pause-btn"
          >
            {t('Save & Pause')}
          </Button>
        )}

        {/* Save & Continue — disabled while paused (server guard preserved) */}
        {currentItem && (
          <Button
            variant="primary"
            onClick={handleContinue}
            isLoading={isSaving}
            rightIcon={<ChevronRight className="h-4 w-4" />}
            disabled={isSaving || isPausing || isResuming || status === 'paused'}
            data-testid="save-continue-btn"
          >
            {t('Save & Continue')}
          </Button>
        )}
      </footer>
    </div>
  );
}
